import { useState, FormEvent, useCallback, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

const MOIS = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];

const JOURS_SEM = ['Di','Lu','Ma','Me','Je','Ve','Sa'];

function getCalendrier(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDaysInMonth = new Date(year, month, 0).getDate();
  const totalSlots = 42;
  const days: { day: number; current: boolean; dateStr: string }[] = [];
  const prevStart = prevDaysInMonth - firstDay + 1;
  for (let i = 0; i < firstDay; i++) {
    days.push({ day: prevStart + i, current: false, dateStr: '' });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    days.push({ day: i, current: true, dateStr: ds });
  }
  const remaining = totalSlots - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ day: i, current: false, dateStr: '' });
  }
  return days;
}

const NETTOYAGE_OPTIONS = [
  { value: 'oui', label: 'Oui' },
  { value: 'non', label: 'Non' },
];

const PERSONALISATION_OPTIONS = [
  { value: 'non', label: 'Non' },
  { value: 'face_a', label: 'Face A' },
  { value: 'face_b', label: 'Face B' },
  { value: 'bandes', label: 'Bandes' },
  { value: 'pack_complet', label: 'Pack complet' },
];

const EVENT_TYPES = [
  { value: 'festivals', label: 'Festivals & grands événements' },
  { value: 'marches', label: 'Marchés & foires' },
  { value: 'manifestations', label: 'Manifestations publiques' },
  { value: 'entreprise', label: "Événements d'entreprise" },
  { value: 'mariages', label: 'Mariages & événements privés' },
  { value: 'marque', label: 'Activations de marque' },
];

const SUPABASE_URL = (import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://lmbvmegvafibwodlweeh.supabase.co').replace(/\/+$/, '');
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
const EMAIL_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/send-devis-email`;

export default function Devis() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  const [nettoyage, setNettoyage] = useState('');
  const [personalisation, setPersonalisation] = useState<string[]>([]);
  const [eventType, setEventType] = useState('');

  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'debut' | 'fin' | null>(null);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());

  const [remorquesCount, setRemorquesCount] = useState('');

  const REMORQUES_COUNT_OPTIONS = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
  ];

  const confirmationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitted && confirmationRef.current) {
      confirmationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitted]);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const days = getCalendrier(calYear, calMonth);

  const clearInvalid = (name: string) => {
    setInvalidFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const isSelected = useCallback(
    (ds: string) => {
      if (!ds) return false;
      if (pickerMode === 'debut') return ds === dateDebut;
      if (pickerMode === 'fin') return ds === dateFin;
      return false;
    },
    [pickerMode, dateDebut, dateFin]
  );

  const isInRange = useCallback(
    (ds: string) => {
      if (!dateDebut || !dateFin || !ds) return false;
      return ds >= dateDebut && ds <= dateFin;
    },
    [dateDebut, dateFin]
  );

  const isDisabled = useCallback(
    (ds: string) => {
      if (!ds) return true;
      return ds < todayStr;
    },
    [todayStr]
  );

  const handleDayClick = (ds: string) => {
    if (!ds || isDisabled(ds)) return;
    if (pickerMode === 'debut') {
      setDateDebut(ds);
      clearInvalid('date_debut');
      if (dateFin && ds > dateFin) setDateFin('');
    } else if (pickerMode === 'fin') {
      if (dateDebut && ds < dateDebut) {
        setDateDebut(ds);
        clearInvalid('date_debut');
        setDateFin('');
      } else {
        setDateFin(ds);
        clearInvalid('date_fin');
      }
    }
    setPickerOpen(false);
    setPickerMode(null);
  };

  const openPicker = (mode: 'debut' | 'fin') => {
    setPickerMode(mode);
    setPickerOpen(true);
    if (mode === 'debut' && dateDebut) {
      const [y, m] = dateDebut.split('-').map(Number);
      setCalYear(y);
      setCalMonth(m - 1);
    } else if (mode === 'fin' && dateFin) {
      const [y, m] = dateFin.split('-').map(Number);
      setCalYear(y);
      setCalMonth(m - 1);
    } else {
      setCalYear(new Date().getFullYear());
      setCalMonth(new Date().getMonth());
    }
  };

  const formatDate = (ds: string) => {
    if (!ds) return '';
    const [y, m, d] = ds.split('-').map(Number);
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
  };

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear(calYear - 1);
    } else {
      setCalMonth(calMonth - 1);
    }
  };

  const nextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear(calYear + 1);
    } else {
      setCalMonth(calMonth + 1);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value;
    if (message && message.length > 500) return;

    const requiredFields = ['nom', 'email', 'telephone', 'societe', 'manifestation', 'localisation_evenement'];
    const newInvalid = new Set<string>();

    requiredFields.forEach((name) => {
      const el = form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement;
      if (!el || el.value.trim() === '') {
        newInvalid.add(name);
      }
    });

    if (!eventType) newInvalid.add('type_evenement');
    if (!dateDebut) newInvalid.add('date_debut');
    if (!dateFin) newInvalid.add('date_fin');
    if (!remorquesCount) newInvalid.add('remorques_count');
    if (!nettoyage) newInvalid.add('nettoyage');
    if (personalisation.length === 0) newInvalid.add('personalisation');

    setInvalidFields(newInvalid);

    if (newInvalid.size > 0) {
      setFormError('Merci de remplir tous les champs obligatoires.');
      return;
    }
    setFormError(null);
    setSubmitting(true);

    try {
      const formData = new FormData(form);
      const payload: Record<string, string> = {};
      formData.forEach((value, key) => {
        payload[key] = value as string;
      });

      // Also include state values that might not be in form elements
      payload.type_evenement = eventType;
      payload.date_debut = dateDebut;
      payload.date_fin = dateFin;
      payload.remorques_count = remorquesCount;
      payload.nettoyage = nettoyage;
      payload.personalisation = personalisation.join(', ');

      const response = await fetch(EMAIL_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMsg = "Erreur lors de l'envoi. Veuillez réessayer.";
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorData.message || errorMsg;
        } catch {
          // ignore JSON parse error
        }
        setFormError(errorMsg);
        return;
      }

      const result = await response.json();

      // Si le mail client n'a pas été envoyé, on affiche un avertissement
      if (result.clientEmailSent === false) {
        setSubmitted(true);
        // Ne pas lancer les confettis en cas d'avertissement
        setFormError(
          "Votre demande a bien été transmise à notre équipe, mais l'email de confirmation n'a pas pu être envoyé. Vérifiez vos spams ou contactez-nous directement."
        );
        return;
      }

      setSubmitted(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00A8B5', '#FFD700', '#FF6B6B', '#4ECDC4', '#96CEB4'],
      });
    } catch {
      setFormError("Impossible d'envoyer la demande. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="devis" className="py-20 sm:py-28 lg:py-16 scroll-mt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 lg:mb-10 animate-fade-in-up delay-100">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-none mb-4"
          >
            Recevoir mon devis
          </h2>
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-10 sm:w-12 bg-[#00A8B5] opacity-40"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-[#00A8B5]"></div>
            <div className="h-px w-10 sm:w-12 bg-[#00A8B5] opacity-40"></div>
          </div>
        </div>
        <p className="text-center text-gray-600 text-base sm:text-lg mb-10 -mt-4">
          Recevez une offre adaptée à votre événement en quelques clics
        </p>

        <div className="animate-fade-in-up delay-300">
          {submitted ? (
            <div ref={confirmationRef} className="flex flex-col items-center justify-center py-12 sm:py-16 text-center animate-fade-in">
              <div className="w-full max-w-lg bg-white border border-gray-200 rounded-xl px-6 sm:px-10 py-8 sm:py-10">
                <div className="w-14 h-14 flex items-center justify-center bg-[#00A8B5]/15 rounded-full mb-5 mx-auto">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-check-line text-[#00A8B5] text-2xl"></i>
                  </div>
                </div>
                <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 leading-snug">
                  Demande envoyée avec succès
                </h4>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4">
                  Nous analysons votre besoin et revenons vers vous sous 24h avec une offre sur mesure.
                </p>

                {formError && (
                  <div className="flex items-start gap-2 text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 text-left">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-amber-600 text-lg"></i>
                    </div>
                    <p>{formError}</p>
                  </div>
                )}

                <p className="text-gray-400 text-xs sm:text-sm">
                  Pensez à vérifier vos emails (y compris vos spams).
                </p>
              </div>
            </div>
          ) : (
            <form
              id="devis-form"
              className="space-y-5 lg:space-y-6"
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Nom / Prénom <span className="text-red-500">*</span></label>
                  <input
                    name="nom"
                    placeholder="Votre nom complet"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('nom') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('nom')}
                  />
                  {invalidFields.has('nom') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">E-mail <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Votre e-mail"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('email') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('email')}
                  />
                  {invalidFields.has('email') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Téléphone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    name="telephone"
                    placeholder="Votre numéro de téléphone"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('telephone') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('telephone')}
                  />
                  {invalidFields.has('telephone') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Raison sociale ou association <span className="text-red-500">*</span></label>
                  <input
                    name="societe"
                    placeholder="Votre entreprise ou association"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('societe') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('societe')}
                  />
                  {invalidFields.has('societe') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Nom de la manifestation <span className="text-red-500">*</span></label>
                  <input
                    name="manifestation"
                    placeholder="Nom de votre manifestation"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('manifestation') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('manifestation')}
                  />
                  {invalidFields.has('manifestation') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Localisation de l'événement <span className="text-red-500">*</span></label>
                  <input
                    name="localisation_evenement"
                    placeholder="Ville ou adresse de l'événement"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('localisation_evenement') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('localisation_evenement')}
                  />
                  {invalidFields.has('localisation_evenement') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
              </div>

              {/* Type d'événement */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">Type d'événement <span className="text-red-500">*</span></label>
                <div className="relative">
                  <select
                    name="type_evenement"
                    value={eventType}
                    onChange={(e) => { setEventType(e.target.value); clearInvalid('type_evenement'); }}
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-sm appearance-none cursor-pointer pr-12 transition-shadow focus:border-[#00A8B5] focus:outline-none ${eventType ? 'text-gray-900' : 'text-gray-500'} ${invalidFields.has('type_evenement') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                  >
                    <option value="" disabled>Sélectionnez votre type d'événement</option>
                    {EVENT_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value} className="text-gray-900">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-500"></i>
                  </div>
                </div>
                {invalidFields.has('type_evenement') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                <div className="relative">
                  <label className="block text-xs text-gray-500 mb-1.5">Date de début <span className="text-red-500">*</span></label>
                  <button
                    type="button"
                    onClick={() => { openPicker('debut'); clearInvalid('date_debut'); }}
                    className={`w-full bg-white border rounded-lg px-4 py-3 text-left text-sm text-gray-900 focus:border-[#00A8B5] focus:outline-none cursor-pointer flex items-center justify-between transition-shadow ${invalidFields.has('date_debut') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                  >
                    <span className={dateDebut ? 'text-gray-900' : 'text-gray-400'}>
                      {dateDebut ? formatDate(dateDebut) : 'Choisir...'}
                    </span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-gray-500"></i>
                    </div>
                  </button>
                  <input type="hidden" name="date_debut" value={dateDebut} />
                  {invalidFields.has('date_debut') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
                <div className="relative">
                  <label className="block text-xs text-gray-500 mb-1.5">Date de fin <span className="text-red-500">*</span></label>
                  <button
                    type="button"
                    onClick={() => { openPicker('fin'); clearInvalid('date_fin'); }}
                    className={`w-full bg-white border rounded-lg px-4 py-3 text-left text-sm text-gray-900 focus:border-[#00A8B5] focus:outline-none cursor-pointer flex items-center justify-between transition-shadow ${invalidFields.has('date_fin') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                  >
                    <span className={dateFin ? 'text-gray-900' : 'text-gray-400'}>
                      {dateFin ? formatDate(dateFin) : 'Choisir...'}
                    </span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-gray-500"></i>
                    </div>
                  </button>
                  <input type="hidden" name="date_fin" value={dateFin} />
                  {invalidFields.has('date_fin') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
              </div>

              {/* Calendrier popup */}
              {pickerOpen && (
                <div className="relative z-50">
                  <div
                    className="absolute left-0 right-0 bg-white border border-gray-300 rounded-xl p-4 shadow-xl"
                    style={{ top: '0' }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <button
                        type="button"
                        onClick={prevMonth}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-left-s-line text-gray-900"></i>
                        </div>
                      </button>
                      <span className="text-gray-900 font-semibold text-sm">
                        {MOIS[calMonth]} {calYear}
                      </span>
                      <button
                        type="button"
                        onClick={nextMonth}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-gray-900"></i>
                        </div>
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {JOURS_SEM.map((j) => (
                        <div key={j} className="text-center text-xs text-gray-400 font-medium py-1">
                          {j}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((d, i) => {
                        const sel = isSelected(d.dateStr);
                        const inR = isInRange(d.dateStr);
                        const dis = isDisabled(d.dateStr);
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => handleDayClick(d.dateStr)}
                            disabled={!d.current || dis}
                            className={`
                              w-9 h-9 text-sm rounded-lg flex items-center justify-center transition-colors cursor-pointer
                              ${!d.current ? 'text-gray-600 cursor-default' : ''}
                              ${d.current && !dis && !sel && !inR ? 'text-gray-900 hover:bg-gray-100' : ''}
                              ${d.current && dis ? 'text-gray-600 cursor-not-allowed' : ''}
                              ${sel ? 'bg-[#00A8B5] text-black font-bold' : ''}
                              ${inR && !sel ? 'bg-[#00A8B5]/15 text-gray-900' : ''}
                            `}
                          >
                            {d.day}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-400">
                        {pickerMode === 'debut' ? 'Sélectionnez le début' : 'Sélectionnez la fin'}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setPickerOpen(false); setPickerMode(null); }}
                        className="text-xs text-[#00A8B5] hover:text-[#008f9b] font-medium cursor-pointer"
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Récapitulatif des dates */}
              {dateDebut && dateFin && (
                <div className="bg-[#00A8B5]/10 border border-[#00A8B5]/30 rounded-lg px-4 py-3 text-center">
                  <p className="text-sm font-medium text-gray-900">
                    Du <span className="font-bold text-[#00A8B5]">{formatDate(dateDebut)}</span> au <span className="font-bold text-[#00A8B5]">{formatDate(dateFin)}</span>
                  </p>
                </div>
              )}

              {/* Nombre de remorques */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">Nombre de remorques souhaitées <span className="text-red-500">*</span></label>
                <div className="flex flex-wrap gap-3">
                  {REMORQUES_COUNT_OPTIONS.map((opt) => {
                    const active = remorquesCount === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setRemorquesCount(opt.value); clearInvalid('remorques_count'); }}
                        className={`
                          min-w-[60px] px-5 py-2.5 rounded-lg border text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap
                          ${active
                            ? 'bg-[#00A8B5] border-[#00A8B5] text-white'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-[#00A8B5]/40 hover:bg-[#00A8B5]/5'}
                          ${invalidFields.has('remorques_count') && !remorquesCount ? 'ring-1 ring-[#D64545] border-[#D64545]' : ''}
                        `}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <input type="hidden" name="remorques_count" value={remorquesCount} />
                {invalidFields.has('remorques_count') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
              </div>
              {/* Nettoyage */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">Nettoyage <span className="text-red-500">*</span></label>
                <div className="flex flex-wrap gap-3">
                  {NETTOYAGE_OPTIONS.map((opt) => {
                    const active = nettoyage === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setNettoyage(opt.value); clearInvalid('nettoyage'); }}
                        className={`
                          px-5 py-2.5 rounded-lg border text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap
                          ${active
                            ? 'bg-[#00A8B5] border-[#00A8B5] text-white'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-[#00A8B5]/40 hover:bg-[#00A8B5]/5'}
                          ${invalidFields.has('nettoyage') && !nettoyage ? 'ring-1 ring-[#D64545] border-[#D64545]' : ''}
                        `}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <input type="hidden" name="nettoyage" value={nettoyage} />
                {invalidFields.has('nettoyage') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
              </div>

              {/* Personnalisation multi-sélection */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">Personnalisation <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(plusieurs choix possibles)</span></label>
                <div className="flex flex-wrap gap-3">
                  {PERSONALISATION_OPTIONS.map((opt) => {
                    const active = personalisation.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          if (opt.value === 'non' || opt.value === 'pack_complet') {
                            setPersonalisation([opt.value]);
                            clearInvalid('personalisation');
                          } else {
                            setPersonalisation((prev) => {
                              const withoutExclusive = prev.filter((v) => v !== 'non' && v !== 'pack_complet');
                              const next = active
                                ? withoutExclusive.filter((v) => v !== opt.value)
                                : [...withoutExclusive, opt.value];
                              return next;
                            });
                            clearInvalid('personalisation');
                          }
                        }}
                        className={`
                          px-5 py-2.5 rounded-lg border text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap
                          ${active
                            ? 'bg-[#00A8B5] border-[#00A8B5] text-white'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-[#00A8B5]/40 hover:bg-[#00A8B5]/5'}
                          ${invalidFields.has('personalisation') && personalisation.length === 0 ? 'ring-1 ring-[#D64545] border-[#D64545]' : ''}
                        `}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <input type="hidden" name="personalisation" value={personalisation.join(', ')} />
                {invalidFields.has('personalisation') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">
                  Un détail pour affiner votre demande ? (facultatif)
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder=""
                  className="w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none resize-none text-sm transition-shadow border-gray-300"
                  onChange={(e) => { setCharCount(e.target.value.length); clearInvalid('message'); }}
                ></textarea>
                <p className="text-right text-xs text-gray-500 mt-1">{charCount}/500</p>
              </div>

              {formError && (
                <div className="flex items-center justify-center gap-2 text-[#D64545] text-sm text-center bg-[#D64545]/10 border border-[#D64545]/30 rounded-lg px-4 py-3 font-semibold">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-[#D64545] text-lg"></i>
                  </div>
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#00A8B5] hover:bg-[#008f9b] text-black font-semibold py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {submitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}